import { addHours, formatDistance, isValid, parseISO } from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';
import * as locales from 'date-fns/locale';
import parse, { Element } from 'html-react-parser';
import { camelCase, isArray, startCase } from 'lodash';
import qs from 'qs';
import { v4 } from 'uuid';
import { MARKET_TIMEZONE, NewsArticleDataType } from '../constants';
import { getCurrentTheme } from '../themes';
import { divide, formatPercentage } from './numberUtils';

// @ts-expect-error: No types available for 'country-iso-3-to-2'
import getCountryISO2 from 'country-iso-3-to-2';

export function getPublicPath(withTimestamp = false): string {
    return `${window.location.protocol}//${window.location.host}${withTimestamp ? `?t=${Date.now()}` : ''}`;
}

export function resolveUrl(url: string) {
    return getPublicPath() + (url.startsWith('/') ? '' : '/') + url;
}

export function zeroPad(num: number, size: number): string {
    let string = num + '';

    while (string.length < size) {
        string = '0' + string;
    }

    return string;
}

export const getUuid = v4;

export function titleCase(value?: string): string {
    return startCase(camelCase(value));
}

export function escapeHtml(content: string, headSpace = 0) {
    return '&nbsp;'.repeat(headSpace) + content.replace(/\n/g, `<br/>${' '.repeat(headSpace)}`).replace(/ /g, '&nbsp;');
}

export function parseOptionSymbol(optionSymbol: string) {
    try {
        // the length of an option symbol has 21 chars
        if (optionSymbol.length !== 21 || (optionSymbol[12] !== 'C' && optionSymbol[12] !== 'P')) {
            return null;
        }

        const symbol = optionSymbol.substr(0, 6).trim();
        const dateString = optionSymbol.substr(6, 6);

        const date = fromZonedTime(
            addHours(
                new Date(
                    Number('20' + dateString.substr(0, 2)),
                    Number(dateString.substr(2, 2)) - 1,
                    Number(dateString.substr(4, 2)),
                ),
                16,
            ),
            MARKET_TIMEZONE,
        );

        const expiration = formatDate(date, { withTimeZone: true, timeZone: MARKET_TIMEZONE, withTime: true });
        const call = optionSymbol[12] === 'C';
        const strike = divide(Number(optionSymbol.substr(13)), 1000);

        return {
            symbol,
            expiration,
            call,
            strike,
        };
    } catch {
        return null;
    }
}

export function parseBondSymbol(bondSymbol: string) {
    try {
        // Check to see if symbol is an isin (12 chars long with 2 letters at the start) or if the symbol is a cusip (9 chars long)
        const isinMatch = bondSymbol.match(/^[A-Z]{2}[0-9]{10}.*/g);
        const cusipMatch = bondSymbol.match(/^[0-9A-Z]{9}.*/g);
        if (!isinMatch && !cusipMatch) {
            return null;
        }
        return {
            isinMatch,
            cusipMatch,
            bondSymbol,
        };
    } catch {
        return null;
    }
}

export function getLocaleByCode(localeCode: string) {
    const localeList = Object.values(locales);
    return localeList.find((item) => item.code === localeCode) || locales.enUS;
}

export function formatDate(
    input?: string | number | Date,
    {
        short = false,
        pattern: initialPattern,
        withTime = false,
        distance = false,
        timeZone,
        localeCode = 'en-US',
        withTimeZone = true,
    }: {
        short?: boolean;
        pattern?: string;
        withTime?: boolean;
        distance?: boolean;
        timeZone?: string;
        localeCode?: string;
        withTimeZone?: boolean;
    } = {},
): string {
    if (!input) {
        return '-';
    }

    let pattern = initialPattern || `MMM dd, yyyy${withTime ? ' HH:mm:ss' : ''}${withTimeZone && timeZone ? ' z' : ''}`;
    if (short) {
        pattern = pattern.replace(', yyyy', '');
    }

    let date: Date;
    let timeZoneData = '';

    if (typeof input === 'string') {
        date = parseISO(input);

        // Not an ISO date
        if (!isValid(date)) {
            const match = input.match(/[a-zA-Z]{2,}$/);
            timeZoneData = match?.[0] ?? '';
            input = input.replace(timeZoneData, '').trim();
        } else {
            const match = input.match(/Z$|[+-]\d{2}:?\d{2}$/);
            if (match && !timeZone) {
                timeZoneData = match[0];
                input = input.replace(timeZoneData, '').trim();

                if (timeZoneData === 'Z' || timeZoneData === '+0000') {
                    timeZoneData = 'UTC';
                } else {
                    timeZoneData = `UTC${timeZoneData}`;
                }
            }
        }
    }

    date = new Date(input);

    if (!isValid(date)) {
        return '-';
    }

    // Return relative time if distance flag is enabled
    if (distance) {
        return formatDistance(date, new Date(), { addSuffix: true });
    }

    // Format date (with optional timezone conversion)
    const formattedDate = format(timeZone ? toZonedTime(date, timeZone) : date, pattern, {
        timeZone,
        locale: localeCode ? getLocaleByCode(localeCode) : undefined,
    });

    if (timeZoneData && withTimeZone) {
        return `${formattedDate} ${timeZoneData}`;
    }

    return formattedDate;
}

type FieldNames<Names extends string, Prefix extends string = ''> = {
    [Name in Names]: Prefix extends '' ? `${Name}` : `${Prefix}-${Name}`;
};
export function generateNamesObject<Names extends string>(name: Names, ...args: Names[]): FieldNames<Names>;
export function generateNamesObject<Names extends string, Prefix extends string = ''>(
    fields: readonly Names[],
    prefix?: Prefix,
): FieldNames<Names, Prefix>;
export function generateNamesObject<Names extends string, Prefix extends string = ''>(
    ...args: unknown[]
): FieldNames<Names, Prefix> {
    let names: Names[] = [];
    let prefix: Prefix | undefined;

    if (isArray(args[0])) {
        names = args[0];
        prefix = args[1] as Prefix;
    } else {
        names = args as Names[];
    }

    return names.reduce(
        (result, name) => {
            result[name] = (prefix ? `${prefix}-${name}` : `${name}`) as FieldNames<Names, Prefix>[Names];
            return result;
        },
        {} as FieldNames<Names, Prefix>,
    );
}

export function getRouteWithParams(route: string, params: Record<string, string | number>): string {
    let newRoute = route;

    Object.keys(params).forEach((key) => {
        newRoute = newRoute.replace(`:${key}`, qs.stringify({ param: params[key] }).replace('param=', ''));
    });

    return newRoute;
}

export const getFraction = (() => {
    //https://github.com/yisibl/num2fraction/blob/master/index.js

    function almostEq(a: number, b: number) {
        return Math.abs(a - b) <= 9.5367432e-7;
    }

    function GCD(a: number, b: number): number {
        if (almostEq(b, 0)) {
            return a;
        }
        return GCD(b, a % b);
    }

    function findPrecision(n: number) {
        let e = 1;

        while (!almostEq(Math.round(n * e) / e, n)) {
            e *= 10;
        }

        return e;
    }

    function num2fraction(num: number | string) {
        if (num === 0 || num === '0') {
            return '0';
        }

        if (typeof num === 'string') {
            num = parseFloat(num);
        }

        const precision = findPrecision(num);
        const number = num * precision;
        const gcd = Math.abs(GCD(number, precision));

        const numerator = number / gcd;
        const denominator = precision / gcd;

        return Math.round(numerator) + ':' + Math.round(denominator);
    }

    return (value: number | string) => num2fraction(value);
})();

export function formatPhoneNumber(phoneNumber: string | undefined, specialChars: boolean) {
    if (phoneNumber === undefined) {
        return;
    }
    if (!specialChars) {
        // return only numbers
        return Number(phoneNumber.replace(/[^\w\s]/gi, ''));
    } else {
        //normalize string and remove all unnecessary characters
        phoneNumber = phoneNumber.replace(/[^\d]/g, '');

        //check if number length equals to 10
        if (phoneNumber.length === 10) {
            //reformat and return phoneNumber number
            return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
        } else if (phoneNumber.length === 11) {
            return phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4');
        }
        return null;
    }
}

export const formatCountryName = (code: string, locale = 'en', style?: Intl.RelativeTimeFormatStyle) => {
    let country: string = code.split('-')[1] || '';

    if (!country) {
        country = code;
    }

    if (country.length === 3) {
        country = getCountryISO2(code);
    }

    const names = new Intl.DisplayNames([locale], {
        type: 'region',
        style,
    });

    return names.of(country);
};

export const formatLanguageName = (code: string, locale = 'en', style?: Intl.RelativeTimeFormatStyle) => {
    const names = new Intl.DisplayNames([locale], {
        type: 'language',
        style,
    });
    return startCase(names.of(code) || '').replace('US English', 'English');
};

export const sentenceCase = (value: string) => {
    return value.charAt(0).toUpperCase() + value.slice(1);
};

export const censor = (value: string, numChar: number, head: boolean) => {
    const regex = new RegExp(`${head ? '^' : ''}.{${numChar}}${!head ? '$' : ''}`);
    return value.replace(regex, '*'.repeat(numChar));
};

export interface NewsArticleData {
    type: string;
    value: string;
    title?: string;
}

export const parseHtml = (
    body?: string,
    options?: {
        articleQuotes?: { symbol: string; changePercent: number }[];
        data?: Record<number, NewsArticleData>;
        onArticleClick?: (articleData: NewsArticleData) => void;
        onSymbolClick?: (symbol: string) => void;
    },
) => {
    if (!body) {
        return '';
    }

    const dataNodeClassname = 'data-node';

    if (options?.articleQuotes) {
        return parse(
            body.replace(/((NYSE|Nasdaq|OTC):\s?)?{(?<index>\d+)}/gi, (...args) => {
                const match = args[0];
                const groups = args[args.length - 1];
                const dataIndex = groups.index;
                const articleData = options.data?.[dataIndex];

                if (!articleData) {
                    return match;
                }

                return `<span class="${dataNodeClassname}" data-index="${dataIndex}"></span>`;
            }),
            {
                replace: (domNode) => {
                    if (domNode instanceof Element && domNode.attribs.class === dataNodeClassname) {
                        const dataIndex = Number(domNode.attribs['data-index']);
                        const articleData = options.data?.[dataIndex];

                        if (!articleData) {
                            return domNode;
                        }

                        if (articleData.type === NewsArticleDataType.Article) {
                            return (
                                <a
                                    style={{
                                        color: getCurrentTheme().palette.primary.main,
                                        cursor: 'pointer',
                                    }}
                                    href={domNode.attribs.href}
                                    onClick={() => options.onArticleClick?.(articleData)}
                                >
                                    {articleData.title}
                                </a>
                            );
                        } else {
                            const quote = options.articleQuotes?.find((quote) => quote.symbol === articleData.value);
                            if (!quote) {
                                return domNode;
                            }

                            return (
                                <span
                                    style={{
                                        color:
                                            quote.changePercent > 0
                                                ? getCurrentTheme().palette.market.up
                                                : getCurrentTheme().palette.market.down,
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <a
                                        style={{
                                            color: getCurrentTheme().palette.primary.main,
                                            fontWeight: 700,
                                        }}
                                        onClick={() => options.onSymbolClick?.(quote.symbol)}
                                    >
                                        {articleData.value}:&nbsp;
                                    </a>
                                    {formatPercentage(quote.changePercent)}
                                </span>
                            );
                        }
                    }

                    return domNode;
                },
            },
        );
    } else {
        return parse(body);
    }
};

export const validatePassword = (password = '') => {
    const hasCapitalLetter = password.toLowerCase() !== password;
    const hasNumbers = /\d+.*\d+/.test(password);
    const hasSpecialCharacter = /[^A-Za-z0-9]/.test(password);
    const hasMinimumLength = password.length >= 8;

    const validated = hasCapitalLetter && hasNumbers && hasSpecialCharacter && hasMinimumLength;

    return {
        hasCapitalLetter,
        hasNumbers,
        hasSpecialCharacter,
        hasMinimumLength,
        validated,
    };
};
